import { useEffect, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import clsx from 'clsx';
import Image from 'next/image';

interface Option {
  image?: {
    src: string;
  };
  label: string;
  value: string;
}

export interface RadioProps {
  title?: string;
  itemsPerRow?: 'grid-cols-1' | 'grid-cols-2' | 'grid-cols-3' | 'grid-cols-4';
  options: Option[];
  fieldName: string;
  formik?: any;
  handleChange?: (fieldName: string, value: string | number) => void;
}

function Radio({
  title,
  itemsPerRow = 'grid-cols-2',
  options,
  fieldName,
  formik,
  handleChange,
}: Readonly<RadioProps>) {
  const { t } = useTranslation('common');
  const [selectedOption, setSelectedOption] = useState<string>('');

  const onSelectOption = (value: string) => {
    setSelectedOption(value);
    handleChange?.(fieldName, value);
  };

  useEffect(() => {
    if (formik.values && formik.values[fieldName] === '') {
      setSelectedOption('');
    } else {
      setSelectedOption(formik.values[fieldName]);
    }
  }, [formik]);

  return (
    <>
      {title && (
        <h4 className="text-textColor text-lg leading-[1.6875rem] font-[700] mb-2">
          {t(title)}
        </h4>
      )}

      <div
        className={clsx('grid gap-[10px]', itemsPerRow, !title && 'mt-[10px]')}
        data-testid="radio-container"
      >
        {options.map((option) => (
          <div
            key={option.value}
            data-testid={`radio-option-${option.value}`}
            className={clsx(
              'border flex flex-col items-center rounded-[10px] cursor-pointer justify-center text-center px-[1px]',
              selectedOption === option.value
                ? 'border-primaryColor bg-radioBgHover text-primaryColor'
                : 'border-grayColor text-textColor',
              option?.image ? 'py-[8px]' : 'py-[15px]'
            )}
            onClick={() => onSelectOption(option.value)}
            onKeyDown={() => onSelectOption(option.value)}
            role="button"
          >
            {option?.image?.src && (
              // eslint-disable-next-line @next/next/no-img-element
              <Image
                src={option.image.src}
                alt={option.value}
                width={50}
                height={25}
                className="mt-0 mb-[5px] h-[25px]"
              />
            )}
            <span className={clsx(option?.image?.src && 'text-sm mt-[5px]')}>
              {t(option.label)}
            </span>
          </div>
        ))}
      </div>
    </>
  );
}

export default Radio;
